import { inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN, TokenInfoViewModel } from '@pxl/models/common';
import { AccountService, LoginService } from '@pxl/models/contracts';
import {
    AccountInitiateregistrationfromselfcheckinBody,
    AccountPendingActivitiesResponse,
    AccountRemovedResponse,
    AccountService as AccountBackendService,
    AddressModel,
    EmailUpdateResultModel,
    FinishRegistrationFromAppointmentReservationResponseModel,
    FinishRegistrationFromCheckInResponseModel,
    InitiateRegistrationFromAppointmentReservationResponseModel,
    InitiateRegistrationFromCheckInResponseModel,
    InvitationStateResult,
    MultiFactorAuthenticationConfirmResponse,
    MultiFactorAuthenticationRemoveResponse,
    MultiFactorAuthenticationService as MultiFactorAuthenticationBackendService,
    MultiFactorAuthenticationSetupResponse,
    PasswordResetResultModel,
    PasswordResetVerificationResultModel,
    PasswordUpdateResultModel,
    PatientDataUpdateModel,
    RegisterResultModel,
    RegistrationModel,
    RegistrationPmsVerificationResult,
    UserModel,
    UserUpdateModel,
    VerifyLinkResultModel,
} from '@pxl/shared/generated';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { UAParser } from 'ua-parser-js';

@Injectable()
export class DefaultAccountService implements AccountService {
    private readonly accountChanged$ = new Subject<void>();

    private readonly loginService = inject(LoginService);
    private readonly accountBackendService = inject(AccountBackendService);
    private readonly multiFactorAuthenticationBackendService = inject(MultiFactorAuthenticationBackendService);
    private readonly config = inject(CONFIG_TOKEN);

    public onAccountChanged(): Observable<void> {
        return this.accountChanged$.asObservable();
    }

    public async initiateRegistrationFromAppointmentReservation(
        userEmail: string,
        password: string,
        user: UserUpdateModel,
        userAddress: AddressModel,
        patients: PatientDataUpdateModel[],
        savedAppointmentReservationId: string,
        veterinarianId: string,
        eula: number,
        privacyPolicy: number,
    ): Promise<InitiateRegistrationFromAppointmentReservationResponseModel> {
        return firstValueFrom(
            this.accountBackendService.initiateRegistrationFromAppointmentReservation(
                {
                    userEmail,
                    password,
                    user,
                    userAddress,
                    patients,
                    savedAppointmentReservationId,
                    veterinarianId,
                    eula,
                    privacyPolicy,
                    whiteLabelId: this.config.whitelabelId,
                },
                this.config.API_VERSION,
            ),
        );
    }

    public async finishRegistrationFromAppointmentReservation(
        clientId: string,
        linkId: string,
    ): Promise<FinishRegistrationFromAppointmentReservationResponseModel> {
        return firstValueFrom(
            this.accountBackendService.finishRegistrationFromAppointmentReservation(
                {
                    linkId,
                    clientId,
                },
                this.config.API_VERSION,
            ),
        );
    }

    public async initiateRegistrationFromCheckIn(
        accountInitiateRegistrationFromSelfCheckIn: AccountInitiateregistrationfromselfcheckinBody,
    ): Promise<InitiateRegistrationFromCheckInResponseModel> {
        return firstValueFrom(
            this.accountBackendService.initiateRegistrationFromCheckIn(
                {
                    ...accountInitiateRegistrationFromSelfCheckIn,
                    whiteLabelId: this.config.whitelabelId,
                },
                this.config.API_VERSION,
            ),
        );
    }

    public async finishRegistrationFromSelfCheckIn(clientId: string, linkId: string): Promise<FinishRegistrationFromCheckInResponseModel> {
        return firstValueFrom(
            this.accountBackendService.finishRegistrationFromCheckIn(
                {
                    linkId,
                    clientId,
                },
                this.config.API_VERSION,
            ),
        );
    }

    public verifyPmsRegistration(
        clientId: string | null,
        emailLink: string,
        password: string | null,
        eula: number | null,
        privacyPolicy: number | null,
        userAddressModel: AddressModel | null,
        userModel: UserModel | null,
    ): Promise<RegistrationPmsVerificationResult> {
        return firstValueFrom(
            this.accountBackendService.registrationPmsVerificationNew(
                {
                    clientId,
                    emailLink,
                    eula,
                    password,
                    privacyPolicy,
                    userAddressModel,
                    userModel,
                },
                this.config.API_VERSION,
            ),
        );
    }

    public registerAccount(registerModel: RegistrationModel): Promise<RegisterResultModel> {
        return firstValueFrom(
            this.accountBackendService.initiateRegistration(
                { ...registerModel, whiteLabelId: this.config.whitelabelId },
                this.config.API_VERSION,
            ),
        );
    }

    public resendEmailVerification(email: string): Promise<void> {
        return firstValueFrom(this.accountBackendService.resendRegistrationLinks({ email }, this.config.API_VERSION));
    }

    public resetPassword(email: string): Promise<PasswordResetResultModel> {
        return firstValueFrom(
            this.accountBackendService.initiatePasswordReset(
                {
                    email,
                    whiteLabelId: this.config.whitelabelId,
                },
                this.config.API_VERSION,
            ),
        );
    }

    public async resetPasswordVerification(linkId: string, newPassword: string): Promise<PasswordResetVerificationResultModel> {
        const clientId = await this.loginService.getClientId();

        return firstValueFrom(
            this.accountBackendService.finishPasswordReset(
                {
                    linkId,
                    newPassword,
                    clientId,
                },
                this.config.API_VERSION,
            ),
        );
    }

    public updateEmail(email: string): Observable<EmailUpdateResultModel> {
        return this.accountBackendService.initiateEmailAddressUpdate({ email }, this.config.API_VERSION, this.loginService.getToken());
    }

    public verifyLink(emailLinkId: string): Observable<VerifyLinkResultModel> {
        return this.accountBackendService.finishEmailAddressUpdate({ emailLinkId }, this.config.API_VERSION, this.loginService.getToken());
    }

    public updatePassword(password: string, newPassword: string): Observable<PasswordUpdateResultModel> {
        return this.accountBackendService.updatePassword(
            {
                password,
                newPassword,
            },
            this.config.API_VERSION,
            this.loginService.getToken(),
        );
    }

    public isMfaEnabled(): Observable<boolean> {
        return this.multiFactorAuthenticationBackendService.isEnabled(this.config.API_VERSION, this.loginService.getToken());
    }

    public setupMfa(): Observable<MultiFactorAuthenticationSetupResponse> {
        return this.multiFactorAuthenticationBackendService.setup(this.config.API_VERSION, this.loginService.getToken());
    }

    public confirmMfa(code: string): Observable<MultiFactorAuthenticationConfirmResponse> {
        return this.multiFactorAuthenticationBackendService
            .confirm({ code }, this.config.API_VERSION, this.loginService.getToken())
            .pipe(tap(() => this.accountChanged$.next()));
    }

    public removeMfa(code?: string, recoveryCode?: string): Observable<MultiFactorAuthenticationRemoveResponse> {
        return this.multiFactorAuthenticationBackendService
            .remove({ code, recoveryCode }, this.config.API_VERSION, this.loginService.getToken())
            .pipe(tap(() => this.accountChanged$.next()));
    }

    public getConnectedDeviceTokens(): Observable<TokenInfoViewModel[]> {
        const userAgentParser = new UAParser();
        return this.accountBackendService.getTokens(this.config.API_VERSION, this.loginService.getToken()).pipe(
            map((tokens) =>
                tokens
                    .map((tokenInfoModel) => {
                        userAgentParser.setUA(tokenInfoModel.userAgent);
                        const result: UAParser.IResult = userAgentParser.getResult();

                        return {
                            token: tokenInfoModel,
                            userAgentInfo: result,
                        };
                    })
                    .sort((a: TokenInfoViewModel, b: TokenInfoViewModel) => (a.token.lastSeenDayUtc < b.token.lastSeenDayUtc ? 1 : -1)),
            ),
        );
    }

    public removeConnectedDevice(tokenId: string): Observable<void> {
        return this.accountBackendService
            .removeToken({ tokenId }, this.config.API_VERSION, this.loginService.getToken())
            .pipe(tap(() => this.accountChanged$.next()));
    }

    public initiateAccountDeletion(forceCancelAppointments: boolean): Observable<boolean> {
        return this.accountBackendService.initiateAccountDeletion(
            this.config.API_VERSION,
            this.loginService.getToken(),
            forceCancelAppointments,
        );
    }

    public async finishAccountDeletion(linkId: string, forceAppointmentRemoval = false): Promise<AccountRemovedResponse> {
        return firstValueFrom(
            this.accountBackendService.finishAccountDeletion(
                { linkId, forceAppointmentRemoval },
                this.config.API_VERSION,
                this.loginService.getToken(),
            ),
        );
    }

    public getPendingAccountActivities(): Observable<AccountPendingActivitiesResponse> {
        return this.accountBackendService.getPendingAccountActivities(this.config.API_VERSION, this.loginService.getToken());
    }

    public getInvitationState(linkId: string): Observable<InvitationStateResult> {
        return this.accountBackendService.getInvitationState(linkId, this.config.API_VERSION);
    }

    public deleteInvitation(linkId: string): Observable<any> {
        return this.accountBackendService.deleteInvitation(linkId, this.config.API_VERSION);
    }
}
